/* ═══════════════════════ Quick Ask (Ctrl+K) ═══════════════════════
   Flytende AI-panel: spør om alt rundt reisen + websøk ved behov.
   - Resizable (begge akser via CSS), drabar header
   - Minimer / Maksimer / Lukk
   - Strukturert markdown-svar med oppfølgingsforslag
   ─────────────────────────────────────────────────────────────────── */

import { streamOnce, executeTool, TOOLS } from './ai.js';
import { esc } from './utils.js';
import { SYSTEM_PROMPT } from './data.js';

const QA_TOOLS = [
  ...TOOLS,
  { type: "web_search_20250305", name: "web_search", max_uses: 5 },
];

const QA_SYSTEM = `${SYSTEM_PROMPT}

VIKTIG OM SVAR-FORMAT (Quick Ask):
- Strukturer svaret i korte avsnitt med Markdown-overskrifter (## Tittel) og punktlister når det passer.
- Hold deg konkret og praktisk. Norsk språk.
- Bruk get_trip_status / get_user_data / get_weather / get_live_rates når brukeren spør om turen, status, vær eller valuta.
- Bruk get_itinerary for dag-for-dag-programmet (klokkeslett, henteadresser, hoteller) og get_documents (query/labels) for det som står i reisedokumentene. Sjekk index/omitted før du sier at noe ikke finnes.
- Bruk web_search KUN når brukeren tydelig trenger fersk eller ekstern informasjon (åpningstider, nyheter, priser, anbefalinger som ikke ligger i reisedata).
- Avslutt ALLTID svaret med en seksjon på nøyaktig dette formatet:

## Oppfølging
- [Q] Forslag til oppfølgingsspørsmål 1
- [Q] Forslag til oppfølgingsspørsmål 2
- [Q] Forslag til oppfølgingsspørsmål 3

Hvert forslag MÅ starte med "[Q] " og være under 70 tegn.`;

let state = {
  open: false,
  minimized: false,
  maximized: false,
  loading: false,
  msgs: [],
  el: null,
};

/* ── Markdown → HTML (lett, trygg) ── */
function md(src){
  const lines = String(src||"").split("\n");
  let html = "";
  let inList = false;
  const closeList = () => { if(inList){ html += "</ul>"; inList = false; } };
  for(let raw of lines){
    const line = raw.replace(/\s+$/,"");
    if(/^##\s+/.test(line)){
      closeList();
      html += `<h3 class="qa-h">${esc(line.replace(/^##\s+/,""))}</h3>`;
    } else if(/^#\s+/.test(line)){
      closeList();
      html += `<h3 class="qa-h">${esc(line.replace(/^#\s+/,""))}</h3>`;
    } else if(/^\s*[-*]\s+/.test(line)){
      if(!inList){ html += "<ul class='qa-ul'>"; inList = true; }
      const item = line.replace(/^\s*[-*]\s+/,"");
      html += `<li>${inline(item)}</li>`;
    } else if(line.trim() === ""){
      closeList();
      html += "<div class='qa-sp'></div>";
    } else {
      closeList();
      html += `<p class="qa-p">${inline(line)}</p>`;
    }
  }
  closeList();
  return html;
}
function inline(s){
  let t = esc(s);
  t = t.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
  t = t.replace(/`([^`]+)`/g, "<code>$1</code>");
  t = t.replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g,
      '<a href="$2" target="_blank" rel="noopener">$1</a>');
  return t;
}

/* ── Trekk ut [Q]-forslag og fjern dem fra synlig tekst ── */
function extractFollowups(text){
  const ups = [];
  const lines = String(text||"").split("\n");
  const kept = [];
  let inSection = false;
  for(const line of lines){
    if(/^##\s*Oppf.lging/i.test(line.trim())){ inSection = true; continue; }
    if(inSection){
      const m = line.match(/^\s*-\s*\[Q\]\s*(.+?)\s*$/);
      if(m){ ups.push(m[1]); continue; }
      if(line.trim() === "" || /^\s*-/.test(line)) continue;
      inSection = false;
      kept.push(line);
    } else {
      kept.push(line);
    }
  }
  return { body: kept.join("\n").replace(/\n{3,}/g,"\n\n").trim(), followups: ups };
}

/* ── DOM ── */
function ensureMounted(){
  if(state.el) return state.el;
  const root = document.createElement("div");
  root.className = "qa-root";
  root.innerHTML = `
    <button class="qa-fab" id="qa-fab" type="button" aria-label="Spør reiseassistent (Alt+K)" title="Spør AI · Alt+K eller /">
      <span class="qa-fab-ic">✨</span>
      <span class="qa-fab-tx">Spør AI</span>
      <span class="qa-fab-kbd">Alt+K</span>
    </button>
    <div class="qa-panel" role="dialog" aria-label="Reiseassistent" id="qa-panel">
      <div class="qa-head" id="qa-head">
        <div class="qa-title"><span class="qa-dot"></span> ✨ Reiseassistent</div>
        <div class="qa-actions">
          <button class="qa-btn" id="qa-min" title="Minimer" aria-label="Minimer">▁</button>
          <button class="qa-btn" id="qa-max" title="Maksimer" aria-label="Maksimer">⛶</button>
          <button class="qa-btn" id="qa-close" title="Lukk (Esc)" aria-label="Lukk">✕</button>
        </div>
      </div>
      <div class="qa-body" id="qa-body">
        <div class="qa-empty">
          <div style="font-size:28px;margin-bottom:6px">🍁</div>
          <div style="font-weight:600;color:var(--t1);margin-bottom:4px">Spør om hva som helst om turen</div>
          <div style="font-size:12px;color:var(--t3);line-height:1.6">
            Dagens program · vær · budsjett · valuta · pakkeliste<br>
            – eller noe utenfor data, så søker jeg på nett.
          </div>
          <div class="qa-quick">
            <button class="qa-chip" data-q="Hva skjer i dag på reisen?">📅 Hva skjer i dag?</button>
            <button class="qa-chip" data-q="Status: hvor langt er jeg på sjekkliste og pakkeliste?">✅ Min status</button>
            <button class="qa-chip" data-q="Hva er dagens valutakurs CAD og USD?">💱 Dagens kurs</button>
            <button class="qa-chip" data-q="Søk opp anbefalte restauranter i Vancouver i 2026.">🌐 Restauranter Vancouver</button>
          </div>
        </div>
      </div>
      <form class="qa-input-row" id="qa-form">
        <textarea id="qa-input" class="qa-input" rows="1" placeholder="Spør om reisen… (Alt+K eller / for å åpne)" aria-label="Spørsmål"></textarea>
        <button type="submit" class="qa-send" id="qa-send" aria-label="Send">↑</button>
      </form>
    </div>
  `;
  document.body.appendChild(root);
  state.el = root;
  bindEvents(root);
  return root;
}

function bindEvents(root){
  root.querySelector("#qa-fab").addEventListener("click", () => openQuickAsk());
  root.querySelector("#qa-close").addEventListener("click", closeQuickAsk);
  root.querySelector("#qa-min").addEventListener("click", toggleMin);
  root.querySelector("#qa-max").addEventListener("click", toggleMax);
  root.querySelector("#qa-form").addEventListener("submit", e => { e.preventDefault(); send(); });
  const input = root.querySelector("#qa-input");
  input.addEventListener("keydown", e => {
    if(e.key === "Enter" && !e.shiftKey){ e.preventDefault(); send(); }
    if(e.key === "Escape"){ closeQuickAsk(); }
  });
  input.addEventListener("input", () => {
    input.style.height = "auto";
    input.style.height = Math.min(input.scrollHeight, 140) + "px";
  });
  root.addEventListener("click", e => {
    const chip = e.target.closest(".qa-chip,[data-followup]");
    if(chip && chip.dataset.q){
      input.value = chip.dataset.q;
      send();
    }
  });
  // Drag
  const head = root.querySelector("#qa-head");
  const panel = root.querySelector("#qa-panel");
  let drag = null;
  head.addEventListener("pointerdown", e => {
    if(e.target.closest(".qa-btn")) return;
    if(state.maximized) return;
    const r = panel.getBoundingClientRect();
    drag = { dx: e.clientX - r.left, dy: e.clientY - r.top };
    head.setPointerCapture(e.pointerId);
  });
  head.addEventListener("pointermove", e => {
    if(!drag) return;
    const x = Math.max(8, Math.min(window.innerWidth - 80, e.clientX - drag.dx));
    const y = Math.max(8, Math.min(window.innerHeight - 60, e.clientY - drag.dy));
    panel.style.left = x + "px";
    panel.style.top = y + "px";
    panel.style.right = "auto";
    panel.style.bottom = "auto";
  });
  head.addEventListener("pointerup", () => { drag = null; });
}

function toggleMin(){
  state.minimized = !state.minimized;
  state.el.querySelector("#qa-panel").classList.toggle("qa-mini", state.minimized);
  if(state.minimized){ state.maximized = false; state.el.querySelector("#qa-panel").classList.remove("qa-max"); }
}
function toggleMax(){
  state.maximized = !state.maximized;
  state.el.querySelector("#qa-panel").classList.toggle("qa-max", state.maximized);
  if(state.maximized){ state.minimized = false; state.el.querySelector("#qa-panel").classList.remove("qa-mini"); }
}

export function openQuickAsk(prefill){
  ensureMounted();
  state.open = true;
  state.el.classList.add("qa-show");
  state.minimized = false;
  state.el.querySelector("#qa-panel").classList.remove("qa-mini");
  setTimeout(() => {
    const inp = state.el.querySelector("#qa-input");
    if(prefill){ inp.value = prefill; }
    inp.focus();
  }, 30);
}
export function closeQuickAsk(){
  if(!state.el) return;
  state.open = false;
  state.el.classList.remove("qa-show");
}
export function toggleQuickAsk(){
  if(state.open) closeQuickAsk(); else openQuickAsk();
}

/* ── Render meldinger ── */
function renderBody(){
  const body = state.el.querySelector("#qa-body");
  if(!state.msgs.length){ return; } // behold empty-state
  body.innerHTML = state.msgs.map((m, i) => {
    if(m.role === "user"){
      return `<div class="qa-msg qa-user"><div class="qa-bubble qa-bubble-user">${esc(m.content)}</div></div>`;
    }
    const { body: text, followups } = extractFollowups(m.content);
    const chips = followups.length ? `<div class="qa-followups">${
      followups.map(q => `<button class="qa-chip qa-follow" data-q="${esc(q).replace(/"/g,'&quot;')}">↪ ${esc(q)}</button>`).join("")
    }</div>` : "";
    return `<div class="qa-msg qa-ai"><div class="qa-bubble qa-bubble-ai">${md(text || m.content)}</div>${i === state.msgs.length-1 ? chips : ""}</div>`;
  }).join("") + (state.loading ? `<div class="qa-msg qa-ai"><div class="qa-bubble qa-bubble-ai"><span class="qa-typ"><span></span><span></span><span></span></span></div></div>` : "");
  body.scrollTop = body.scrollHeight;
}

async function send(){
  const inp = state.el.querySelector("#qa-input");
  const q = (inp.value || "").trim();
  if(!q || state.loading) return;
  inp.value = "";
  inp.style.height = "auto";

  state.msgs.push({ role: "user", content: q });
  state.msgs.push({ role: "ai", content: "" });
  state.loading = true;
  renderBody();

  if(!navigator.onLine){
    state.msgs[state.msgs.length-1].content = "📡 Offline — Quick Ask trenger nettforbindelse.";
    state.loading = false;
    renderBody();
    return;
  }

  let apiMessages = state.msgs.slice(0, -1).map(m => ({
    role: m.role === "ai" ? "assistant" : "user",
    content: m.content,
  }));

  const aiIdx = state.msgs.length - 1;
  const onText = (delta) => {
    state.msgs[aiIdx].content += delta;
    const body = state.el.querySelector("#qa-body");
    const last = body.querySelectorAll(".qa-bubble-ai");
    if(last.length){
      const { body: text } = extractFollowups(state.msgs[aiIdx].content);
      last[last.length-1].innerHTML = md(text || state.msgs[aiIdx].content);
      body.scrollTop = body.scrollHeight;
    }
  };

  try {
    let iterations = 0;
    while(iterations++ < 6){
      const { blocks, stopReason } = await streamOnce(apiMessages, QA_SYSTEM, QA_TOOLS, onText);
      if(stopReason !== "tool_use") break;
      apiMessages.push({ role: "assistant", content: blocks });
      const toolResults = [];
      for(const b of blocks){
        if(b.type !== "tool_use") continue;
        const result = await executeTool(b.name, b.input || {});
        toolResults.push({
          type: "tool_result",
          tool_use_id: b.id,
          content: typeof result === "string" ? result : JSON.stringify(result),
        });
      }
      apiMessages.push({ role: "user", content: toolResults });
    }
  } catch(e){
    state.msgs[aiIdx].content += "\n\n⚠️ Feil: " + (e.message || "kunne ikke koble til.");
  }
  state.loading = false;
  renderBody();
}

/* ── Global hotkey ──
   Ctrl/Cmd+K er reservert av nettleseren (adressefelt). Vi bruker Alt+K,
   og «/» som rask snarvei når ingen input er fokusert. */
function isTypingTarget(t){
  if(!t) return false;
  const tag = t.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || t.isContentEditable;
}
function syncSplashClass(){
  const sp = document.getElementById("splash");
  const visible = sp && getComputedStyle(sp).display !== "none" && getComputedStyle(sp).opacity !== "0";
  document.body.classList.toggle("qa-splash-on", !!visible);
}
export function bindQuickAskHotkey(){
  // Monter FAB-launcher med en gang så brukeren ser at funksjonen finnes
  if(document.body){ ensureMounted(); syncSplashClass(); }
  else document.addEventListener("DOMContentLoaded", () => { ensureMounted(); syncSplashClass(); }, { once: true });
  // Følg med på splash-endringer (style/class)
  const sp = document.getElementById("splash");
  if(sp){
    new MutationObserver(syncSplashClass).observe(sp, { attributes: true, attributeFilter: ["style","class"] });
  }
  window.addEventListener("keydown", e => {
    // Alt+K (eller Alt+Shift+K) – uavhengig av fokus
    if(e.altKey && (e.key === "k" || e.key === "K")){
      e.preventDefault();
      toggleQuickAsk();
      return;
    }
    // «/» når man ikke skriver i et felt
    if(e.key === "/" && !e.ctrlKey && !e.metaKey && !e.altKey && !isTypingTarget(e.target)){
      e.preventDefault();
      openQuickAsk();
      return;
    }
  });
}
